import { Code2, PanelsTopLeft, X } from 'lucide-react'
import { useEffect } from 'react'

import adkPreviewImg from '/bot-type-adk-preview.png'
import studioPreviewImg from '/bot-type-studio-preview.png'
import { trackEvent } from '~/providers'

export type BotType = 'studio' | 'adk' | 'vibe'

export const DEFAULT_BOT_TYPES = ['studio', 'adk'] as const satisfies BotType[]

type BotTypeSelectionOverlayProps = {
  onSelect: (type: BotType) => void
  onClose: () => void
  dismissable?: boolean
  botTypes?: readonly BotType[]
}

type BotTypeSelectionScreenProps = {
  onSelect: (type: BotType) => void
  onClose?: () => void
  dismissable?: boolean
  botTypes?: readonly BotType[]
}

const trackBotTypeDialogPresented = () => trackEvent({ type: 'bot_type_dialog_presented' })
const trackBotTypeSelected = (botType: BotType) => trackEvent({ type: 'bot_type_selected', botType })
const trackBotTypeDialogDismissed = () => trackEvent({ type: 'bot_type_dialog_dismissed' })

const useBotTypeSelectionEffects = ({
  dismissable,
  onClose,
}: Pick<BotTypeSelectionScreenProps, 'dismissable' | 'onClose'>) => {
  useEffect(() => {
    trackBotTypeDialogPresented()
  }, [])

  useEffect(() => {
    if (!dismissable || !onClose) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [dismissable, onClose])
}

export const BotTypeSelectionScreen = ({
  onSelect,
  onClose,
  dismissable = true,
  botTypes = DEFAULT_BOT_TYPES,
}: BotTypeSelectionScreenProps) => {
  useBotTypeSelectionEffects({ dismissable, onClose })
  const showVibe = botTypes.includes('vibe')
  const showStudio = botTypes.includes('studio')
  const showAdk = botTypes.includes('adk')
  const showSecondaryOptions = (showVibe && showStudio) || showAdk

  return (
    <div className="relative w-full overflow-hidden rounded-[1.75rem] border border-border/70 bg-card/95 shadow-[0_32px_100px_-48px_rgba(15,23,42,0.7)] backdrop-blur">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,#dbeafe_0%,transparent_45%),radial-gradient(circle_at_bottom,#eff6ff_0%,transparent_38%)] opacity-90 dark:bg-[radial-gradient(circle_at_top,#172554_0%,transparent_50%),radial-gradient(circle_at_bottom,#0f172a_0%,transparent_40%)]" />
      <div className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="relative flex flex-col gap-4 p-4 sm:gap-5 sm:p-5 md:p-6">
        {dismissable && onClose ? (
          <button
            type="button"
            aria-label="Close bot type selection"
            onClick={onClose}
            className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/80 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}

        <div className="flex flex-col items-center gap-2 text-center">
          <div className="space-y-2">
            <h1
              id="bot-type-dialog-title"
              className="font-[Aspekta] text-3xl font-semibold tracking-tight text-balance text-foreground sm:text-4xl lg:text-[3.5rem]"
            >
              How do you want to build?
            </h1>
            <p className="mx-auto max-w-2xl text-sm leading-5 text-muted-foreground sm:leading-6">
              Choose what&apos;s best for you. This can&apos;t be changed later.
            </p>
          </div>
        </div>

        {showVibe ? (
          <div className="group relative">
            <div className="pointer-events-none absolute -inset-2 rounded-[1.75rem] bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-22" />
            <button
              type="button"
              onClick={() => onSelect('vibe')}
              className="relative flex w-full flex-col gap-3 overflow-hidden rounded-[1.5rem] border border-primary/20 bg-background/85 p-4 text-left transition-all duration-200 hover:-translate-y-px hover:border-primary/30 hover:shadow-[0_22px_48px_-52px_rgba(59,130,246,0.72)] sm:p-5 md:gap-4 md:p-5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-primary/12 opacity-80" />
              <div className="flex flex-col gap-3">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-xl font-semibold text-foreground sm:text-2xl">Botpress Vibe</h2>
                    <span className="rounded-md bg-emerald-500/12 px-2.5 py-1 text-sm font-semibold text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300">
                      Recommended
                    </span>
                  </div>
                  <p className="max-w-2xl text-sm leading-5 text-muted-foreground sm:leading-6">
                    Chat with an AI builder and preview the bot as it takes shape.
                  </p>
                </div>
              </div>

              <div className="relative grid min-h-40 overflow-hidden rounded-[1rem] border border-border/70 bg-slate-950 p-4 text-slate-100 sm:min-h-48 md:grid-cols-[minmax(0,1.1fr)_minmax(260px,0.9fr)] md:gap-4">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.24),transparent_34%),radial-gradient(circle_at_82%_12%,rgba(59,130,246,0.28),transparent_34%),linear-gradient(135deg,rgba(15,23,42,0.84),rgba(2,6,23,0.98))]" />
                <div className="relative flex flex-col justify-end gap-3">
                  <div className="w-fit rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1 text-xs font-medium text-cyan-100">
                    Vibe builder
                  </div>
                  <div className="max-w-md rounded-2xl border border-white/10 bg-white/10 p-3 shadow-2xl backdrop-blur">
                    <p className="text-sm leading-5 text-slate-100">
                      Build a lead qualification agent for our enterprise prospects.
                    </p>
                  </div>
                  <div className="ml-8 max-w-lg rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 shadow-2xl backdrop-blur">
                    <p className="text-sm leading-5 text-cyan-50">
                      I&apos;ll draft the bot, connect the handoff path, and show you a live preview.
                    </p>
                  </div>
                </div>
                <div className="relative mt-4 hidden rounded-2xl border border-white/10 bg-white/[0.08] p-3 shadow-2xl backdrop-blur md:block">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-8 rounded-lg bg-white/[0.14]" />
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-20 rounded-lg bg-cyan-300/[0.14]" />
                      <div className="h-20 rounded-lg bg-white/10" />
                    </div>
                    <div className="h-10 rounded-lg bg-blue-300/[0.14]" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col text-xs">
                <span className="font-medium tracking-[0.18em] text-muted-foreground uppercase">Best for</span>
                <span className="mt-1 text-sm text-muted-foreground">Guided bot creation with an AI builder.</span>
              </div>
            </button>
          </div>
        ) : showStudio ? (
          <div className="group relative">
            <div className="pointer-events-none absolute -inset-2 rounded-[1.75rem] bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-22" />
            <button
              type="button"
              onClick={() => onSelect('studio')}
              className="relative flex w-full flex-col gap-3 overflow-hidden rounded-[1.5rem] border border-primary/20 bg-background/85 p-4 text-left transition-all duration-200 hover:-translate-y-px hover:border-primary/30 hover:shadow-[0_22px_48px_-52px_rgba(59,130,246,0.72)] sm:p-5 md:gap-4 md:p-5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-primary/12 opacity-80" />
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl font-semibold text-foreground sm:text-2xl">Botpress Studio</h2>
                  <span className="rounded-md bg-emerald-500/12 px-2.5 py-1 text-sm font-semibold text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300">
                    Recommended
                  </span>
                </div>
                <p className="max-w-2xl text-sm leading-5 text-muted-foreground sm:leading-6">
                  Build bots visually with a drag and drop canvas.
                </p>
              </div>

              <div className="relative overflow-hidden rounded-[1rem]">
                <img
                  src={studioPreviewImg}
                  alt="Botpress Studio preview"
                  className="h-[24vh] max-h-64 min-h-40 w-full rounded-[1rem] object-cover sm:h-[26vh] sm:max-h-72"
                />
              </div>

              <div className="flex flex-col text-xs">
                <span className="font-medium tracking-[0.18em] text-muted-foreground uppercase">Best for</span>
                <span className="mt-1 text-sm text-muted-foreground">Most use cases.</span>
              </div>
            </button>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {showSecondaryOptions ? (
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium tracking-[0.18em] text-muted-foreground uppercase">
                Or
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
          ) : null}

          {showVibe && showStudio ? (
            <div className="group relative">
              <div className="pointer-events-none absolute -inset-2 rounded-[1.5rem] bg-gradient-to-r from-cyan-500 via-sky-500 to-emerald-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-18" />
              <button
                type="button"
                onClick={() => onSelect('studio')}
                className="relative flex w-full flex-col gap-3 rounded-[1.25rem] border border-transparent bg-transparent p-1 text-left transition-colors hover:border-border/65 hover:bg-background/40 sm:p-2 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors group-hover:text-foreground">
                    <PanelsTopLeft className="h-[18px] w-[18px]" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground sm:text-base">I want the canvas</span>
                      <span className="text-sm font-semibold text-foreground underline decoration-border underline-offset-4 sm:text-base">
                        Use Botpress Studio
                      </span>
                    </div>
                    <p className="max-w-2xl text-sm leading-5 text-muted-foreground">
                      Build bots visually with a drag and drop canvas.
                    </p>
                  </div>
                </div>

                <div className="hidden overflow-hidden rounded-xl md:block md:w-40">
                  <img
                    src={studioPreviewImg}
                    alt="Botpress Studio preview"
                    className="w-full rounded-xl object-cover"
                  />
                </div>
              </button>
            </div>
          ) : null}

          {showAdk ? (
            <div className="group relative">
              <div className="pointer-events-none absolute -inset-2 rounded-[1.5rem] bg-gradient-to-r from-purple-500 via-violet-500 to-fuchsia-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-18" />
              <button
                type="button"
                onClick={() => onSelect('adk')}
                className="relative flex w-full flex-col gap-3 rounded-[1.25rem] border border-transparent bg-transparent p-1 text-left transition-colors hover:border-border/65 hover:bg-background/40 sm:p-2 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors group-hover:text-foreground">
                    <Code2 className="h-[18px] w-[18px]" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground sm:text-base">
                        I&apos;m a developer
                      </span>
                      <span className="text-sm font-semibold text-foreground underline decoration-border underline-offset-4 sm:text-base">
                        Use the Agent Development Kit
                      </span>
                      <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                        Advanced
                      </span>
                      <span className="rounded border border-blue-500 bg-blue-950 px-2 py-0.5 text-xs font-semibold text-blue-400">
                        Beta
                      </span>
                    </div>
                    <p className="max-w-2xl text-sm leading-5 text-muted-foreground">
                      Build agents in TypeScript with full control.
                    </p>
                  </div>
                </div>

                <div className="hidden overflow-hidden rounded-xl md:block md:w-40">
                  <img
                    src={adkPreviewImg}
                    alt="Agent Development Kit preview"
                    className="w-full rounded-xl object-cover"
                  />
                </div>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const BotTypeSelectionOverlay = ({ onSelect, onClose, dismissable = true, botTypes }: BotTypeSelectionOverlayProps) => {
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (dismissable && event.target === event.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bot-type-dialog-title"
      className="fixed inset-0 z-50 overflow-y-auto bg-background/80 backdrop-blur-sm"
    >
      <div
        className="flex min-h-dvh items-center justify-center px-3 py-3 sm:px-4 sm:py-4"
        onClick={handleBackdropClick}
      >
        <div className="w-full max-w-6xl">
          <BotTypeSelectionScreen onSelect={onSelect} onClose={onClose} dismissable={dismissable} botTypes={botTypes} />
        </div>
      </div>
    </div>
  )
}

type ShowBotTypeSelectionDialogOptions = {
  dismissable?: boolean
  botTypes?: readonly BotType[]
}

export const showBotTypeSelectionDialog = (
  options: ShowBotTypeSelectionDialogOptions = {}
): Promise<BotType | undefined> => {
  const { dismissable = true, botTypes } = options

  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)

    let reactRoot: { unmount: () => void; render: (children: React.ReactNode) => void } | null = null

    const cleanup = () => {
      reactRoot?.unmount()
      container.remove()
    }

    const handleSelect = (type: BotType) => {
      trackBotTypeSelected(type)
      cleanup()
      resolve(type)
    }

    const handleClose = () => {
      trackBotTypeDialogDismissed()
      cleanup()
      resolve(undefined)
    }

    import('react-dom/client').then(({ createRoot }) => {
      reactRoot = createRoot(container)
      reactRoot.render(
        <BotTypeSelectionOverlay
          onSelect={handleSelect}
          onClose={handleClose}
          dismissable={dismissable}
          botTypes={botTypes}
        />
      )
    })
  })
}
